/**
 * AP-46 11.2 — **Einstellungen der Baustellendokumente** (`/settings/site-docs`, `sitedocs.settings.manage`):
 * je Art Präfix, Muster, Stellen, Blockgröße, Bestätigungstext, Fotos, Pflicht-Unterzeichner, erlaubte
 * Freigaben, Frist und Erinnerungen der Bestätigung per Link — feldweise ohne Knopf (AP-36). Dazu
 * Nummernblöcke (wer hält was, zurückziehen), Nummernlücken und die Quarantäne.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Settings } from 'lucide-react'
import { Modal } from '@symphenia/ui-components'
import { SITE_DOC_RELEASES, SITE_DOC_SIGNER_ROLES, type SiteDocKind, type SiteDocKindSettings, type SiteDocRelease, type SiteDocSignerRole } from '@symphenia/shared-types'
import { bestaetige } from '@/lib/dialoge'
import { fehlertext } from '@/lib/fehlertext'
import { fmtDateTime } from '@/lib/format-date'
import { pageWide } from '@/lib/form-styles'
import { api } from '@/lib/axios'
import { Abschnitt, FeldZeile, feldKlasse, mitStand, useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { cn } from '@/lib/utils'
import { Hausregeln } from '@/components/ki/Hausregeln'
import { useAuthStore } from '@/lib/auth.store'
import { erpArt } from '../arten/registry'
import { KiEinstellung } from '../components/KiEinstellung'
import '../arten'
import {
  fetchNumberBlocks,
  fetchNumberGaps,
  fetchQuarantine,
  fetchQuarantineEntry,
  fetchSiteDocSettings,
  resolveQuarantine,
  withdrawNumberBlock,
  type NumberBlockRow,
  type NumberGapRow,
  type QuarantineRow,
} from '../api/site-docs.api'

type Reiter = 'kinds' | 'blocks' | 'gaps' | 'quarantine' | 'ai'

const tageLesen = (text: string): number[] =>
  [...new Set(text.split(/[,;\s]+/).map((x) => Number(x)).filter((n) => Number.isInteger(n) && n > 0 && n <= 60))].sort((a, b) => a - b)

function ArtEinstellungen({ start }: { start: SiteDocKindSettings }) {
  const { t } = useTranslation()
  const [stand, setStand] = useState(start)
  const daten = useMemo(() => ({ ...stand, linkReminderDays: stand.linkReminderDays.join(', ') }) as unknown as Datensatz, [stand])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `siteDocSettings:${start.kind}`,
    bezeichnung: t('siteDocs.settings.of', { art: t(`siteDocs.kinds.${start.kind}`) }),
    daten,
    schreibe: async (patch, s) => {
      const body = { ...patch } as Record<string, unknown>
      if (typeof body.linkReminderDays === 'string') body.linkReminderDays = tageLesen(body.linkReminderDays)
      const r = await api.patch<{ data: SiteDocKindSettings }>(`/site-docs/settings/kinds/${start.kind}`, body, mitStand(s))
      setStand(r.data.data)
      return { ...r.data.data, linkReminderDays: r.data.data.linkReminderDays.join(', ') } as unknown as Datensatz
    },
    keepalive: { url: `/site-docs/settings/kinds/${start.kind}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const prefix = useFeld<string | null>(stelle, 'prefix', { label: t('siteDocs.settings.prefix'), pflicht: true })
  const muster = useFeld<string | null>(stelle, 'numberPattern', { label: t('siteDocs.settings.pattern'), pflicht: true, pruefe: (w) => (w && w.includes('{nummer}') ? null : t('siteDocs.settings.patternNeedsNumber')) })
  const stellen = useFeld<number | null>(stelle, 'digits', { art: 'zahl', label: t('siteDocs.settings.digits'), pflicht: true })
  const block = useFeld<number | null>(stelle, 'blockSize', { art: 'zahl', label: t('siteDocs.settings.blockSize'), pflicht: true })
  const text = useFeld<string | null>(stelle, 'confirmationText', { label: t('siteDocs.settings.confirmationText') })
  const minFotos = useFeld<number | null>(stelle, 'minPhotos', { art: 'zahl', label: t('siteDocs.settings.minPhotos'), pflicht: true })
  const maxFotos = useFeld<number | null>(stelle, 'maxPhotos', { art: 'zahl', label: t('siteDocs.settings.maxPhotos'), pflicht: true })
  const frist = useFeld<number | null>(stelle, 'linkValidDays', { art: 'zahl', label: t('siteDocs.settings.linkValidDays'), pflicht: true })
  const erinnern = useFeld<string | null>(stelle, 'linkReminderDays', { label: t('siteDocs.settings.linkReminderDays') })
  const Eigene = erpArt(start.kind)?.Einstellungen

  /** Listen gehen als Ganzes — die Operation läuft bei einem Konflikt auf dem aktuellen Stand neu (AP-36 8.3 a). */
  function umschalten<T extends string>(feld: 'requiredSigners' | 'allowedReleases', wert: T) {
    const liste = stand[feld] as string[]
    const neu = liste.includes(wert) ? liste.filter((x) => x !== wert) : [...liste, wert]
    if (feld === 'allowedReleases' && !neu.length) return
    setStand((s) => ({ ...s, [feld]: neu }))
    void stelle.sende({ kennung: feld, felder: [feld], labels: { [feld]: t(`siteDocs.settings.${feld}`) }, patch: { [feld]: neu } })
  }

  return (
    <Abschnitt titel={t(`siteDocs.kinds.${start.kind}`)}>
      <>
        <FeldZeile label={t('siteDocs.settings.prefix')} status={prefix}>
          <input className={feldKlasse} {...prefix.props} />
        </FeldZeile>
        <FeldZeile label={t('siteDocs.settings.pattern')} status={muster} hilfe={t('siteDocs.settings.patternHelp')}>
          <input className={feldKlasse} {...muster.props} />
        </FeldZeile>
        <FeldZeile label={t('siteDocs.settings.digits')} status={stellen}>
          <input className={`${feldKlasse} w-28 text-right`} inputMode="numeric" {...stellen.props} />
        </FeldZeile>
        <FeldZeile label={t('siteDocs.settings.blockSize')} status={block} hilfe={t('siteDocs.settings.blockSizeHelp')}>
          <input className={`${feldKlasse} w-28 text-right`} inputMode="numeric" {...block.props} />
        </FeldZeile>
      </>
      <FeldZeile label={t('siteDocs.settings.confirmationText')} status={text} hilfe={t('siteDocs.settings.confirmationTextHelp')}>
        <textarea className={`${feldKlasse} min-h-20`} {...text.props} />
      </FeldZeile>
      <>
        <FeldZeile label={t('siteDocs.settings.minPhotos')} status={minFotos}>
          <input className={`${feldKlasse} w-28 text-right`} inputMode="numeric" {...minFotos.props} />
        </FeldZeile>
        <FeldZeile label={t('siteDocs.settings.maxPhotos')} status={maxFotos}>
          <input className={`${feldKlasse} w-28 text-right`} inputMode="numeric" {...maxFotos.props} />
        </FeldZeile>
        <FeldZeile label={t('siteDocs.settings.linkValidDays')} status={frist}>
          <input className={`${feldKlasse} w-28 text-right`} inputMode="numeric" {...frist.props} />
        </FeldZeile>
        <FeldZeile label={t('siteDocs.settings.linkReminderDays')} status={erinnern} hilfe={t('siteDocs.settings.linkReminderDaysHelp')}>
          <input className={feldKlasse} {...erinnern.props} />
        </FeldZeile>
      </>
      <div className="grid gap-3 pt-2 sm:grid-cols-2">
        <fieldset className="space-y-1 text-sm">
          <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('siteDocs.settings.requiredSigners')}</legend>
          {SITE_DOC_SIGNER_ROLES.map((r) => (
            <label key={r} className="flex items-center gap-2">
              <input type="checkbox" checked={stand.requiredSigners.includes(r)} onChange={() => umschalten<SiteDocSignerRole>('requiredSigners', r)} /> {t(`siteDocs.roles.${r}`)}
            </label>
          ))}
        </fieldset>
        <fieldset className="space-y-1 text-sm">
          <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('siteDocs.settings.allowedReleases')}</legend>
          {SITE_DOC_RELEASES.map((r) => (
            <label key={r} className="flex items-center gap-2">
              <input type="checkbox" checked={stand.allowedReleases.includes(r)} onChange={() => umschalten<SiteDocRelease>('allowedReleases', r)} /> {t(`siteDocs.releases.${r}`)}
            </label>
          ))}
        </fieldset>
      </div>
      {Eigene && (
        <Eigene
          extra={stand.extra}
          speichere={(extra) => {
            setStand((s) => ({ ...s, extra }))
            return stelle.sende({ kennung: 'extra', felder: ['extra'], labels: { extra: t('siteDocs.settings.kindSettings') }, patch: { extra } })
          }}
        />
      )}
    </Abschnitt>
  )
}

/** Grund der Quarantäne: der Fehlercode des Servers (`SITE_DOC_HASH_MISMATCH`) → `siteDocs.errors.hashMismatch`. */
function quarantaeneGrund(t: (key: string, o?: Record<string, unknown>) => string, code: string | null | undefined): string {
  if (!code) return ''
  const schluessel = code.replace(/^SITE_DOC_/, '').toLowerCase().replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
  return t(`siteDocs.errors.${schluessel}`, { defaultValue: t('siteDocs.errors.hashMismatch') })
}

function Tabelle({ kopf, children, leer }: { kopf: string[]; children: React.ReactNode; leer: boolean }) {
  const { t } = useTranslation()
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/50">
          <tr>
            {kopf.map((k) => (
              <th key={k} className="px-4 py-1.5 text-left font-medium">
                {k}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {leer && (
            <tr>
              <td colSpan={kopf.length} className="px-4 py-6 text-center text-muted-foreground">
                {t('siteDocs.settings.nothing')}
              </td>
            </tr>
          )}
          {children}
        </tbody>
      </table>
    </div>
  )
}

export default function SiteDocsSettingsPage() {
  const { t } = useTranslation()
  const [reiter, setReiter] = useState<Reiter>('kinds')
  // AP-46 21.4: der Reiter „Diktat mit KI" nur mit gebuchtem `SITE_DOCS.AI`.
  const { hasModule } = useAuthStore()
  const reiterListe: Reiter[] = ['kinds', 'blocks', 'gaps', 'quarantine', ...(hasModule('SITE_DOCS.AI') ? (['ai'] as Reiter[]) : [])]
  const [arten, setArten] = useState<SiteDocKindSettings[] | null>(null)
  const [bloecke, setBloecke] = useState<NumberBlockRow[]>([])
  const [luecken, setLuecken] = useState<NumberGapRow[]>([])
  const [quarantaene, setQuarantaene] = useState<QuarantineRow[]>([])
  const [ansicht, setAnsicht] = useState<{ zeile: QuarantineRow; inhalt: Record<string, unknown> } | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const lade = useCallback(async (r: Reiter) => {
    setFehler(null)
    try {
      if (r === 'kinds') setArten(await fetchSiteDocSettings())
      if (r === 'blocks') setBloecke(await fetchNumberBlocks())
      if (r === 'gaps') setLuecken(await fetchNumberGaps())
      if (r === 'quarantine') setQuarantaene(await fetchQuarantine())
    } catch (e) {
      setFehler(fehlertext(e, t))
    }
  }, [t])

  useEffect(() => {
    void lade(reiter)
  }, [reiter, lade])

  const art = (k: SiteDocKind) => t(`siteDocs.kinds.${k}`)

  return (
    <div className={pageWide}>
      <h1 className="flex items-center gap-2 text-3xl font-bold text-foreground">
        <Settings className="h-7 w-7" aria-hidden /> {t('siteDocs.settings.title')}
      </h1>
      <div className="flex flex-wrap gap-1 border-b border-border">
        {reiterListe.map((r) => (
          <button key={r} type="button" onClick={() => setReiter(r)} className={cn('-mb-px border-b-2 px-3 py-2 text-sm', reiter === r ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}>
            {t(`siteDocs.settings.tabs.${r}`)}
          </button>
        ))}
      </div>
      {fehler && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}

      {reiter === 'kinds' && arten?.map((a) => <ArtEinstellungen key={a.kind} start={a} />)}
      {reiter === 'ai' && (
        <>
          <KiEinstellung />
          {/* AP-44: Hausregeln der Diktat-Aufgaben (AP-27 18.) — gelten, sobald das Diktat eingeschaltet ist. */}
          <Hausregeln pfad="/site-docs/settings/ai/house-rules" />
        </>
      )}

      {reiter === 'blocks' && (
        <Tabelle kopf={[t('siteDocs.columns.kind'), t('siteDocs.settings.year'), t('siteDocs.settings.range'), t('siteDocs.settings.used'), t('siteDocs.settings.holder'), t('siteDocs.settings.device'), t('siteDocs.settings.reserved'), '']} leer={!bloecke.length}>
          {bloecke.map((b) => (
            <tr key={b.id} className="border-b border-border last:border-0">
              <td className="px-4 py-1.5">{art(b.kind)}</td>
              <td className="px-4 py-1.5 tabular-nums">{b.year}</td>
              <td className="px-4 py-1.5 tabular-nums">
                {b.firstValue}–{b.lastValue}
              </td>
              <td className="px-4 py-1.5 tabular-nums">{b.used}</td>
              <td className="px-4 py-1.5">{b.userName}</td>
              <td className="px-4 py-1.5 font-mono text-xs">{b.deviceId.slice(0, 8)}</td>
              <td className="px-4 py-1.5">{fmtDateTime(b.reservedAt)}</td>
              <td className="px-4 py-1.5 text-right">
                {b.withdrawnAt ? (
                  <span className="text-xs text-muted-foreground">{t('siteDocs.settings.withdrawn')}</span>
                ) : b.returnedAt ? (
                  <span className="text-xs text-muted-foreground">{t('siteDocs.settings.returned')}</span>
                ) : (
                  <button
                    type="button"
                    className="text-xs text-red-700 hover:underline"
                    onClick={async () => {
                      if (!(await bestaetige(t('siteDocs.settings.withdrawConfirm'), { ok: t('siteDocs.settings.withdraw'), gefaehrlich: true }))) return
                      try {
                        await withdrawNumberBlock(b.id)
                        await lade('blocks')
                      } catch (e) {
                        setFehler(fehlertext(e, t))
                      }
                    }}
                  >
                    {t('siteDocs.settings.withdraw')}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </Tabelle>
      )}

      {reiter === 'gaps' && (
        <Tabelle kopf={[t('siteDocs.columns.number'), t('siteDocs.columns.kind'), t('siteDocs.settings.reason'), t('siteDocs.settings.holder'), t('siteDocs.settings.since')]} leer={!luecken.length}>
          {luecken.map((l) => (
            <tr key={`${l.kind}-${l.number}`} className="border-b border-border last:border-0">
              <td className="px-4 py-1.5 tabular-nums">{l.number}</td>
              <td className="px-4 py-1.5">{art(l.kind)}</td>
              <td className="px-4 py-1.5">
                {t(`siteDocs.settings.gapReasons.${l.reason}`)}
                {/* Beim unverbrauchten Block ist der Vermerk ein Schlüssel (withdrawn/returned), sonst der Grund des Benutzers. */}
                {l.note ? ` — ${l.reason === 'BLOCK_UNUSED' ? t(`siteDocs.settings.${l.note}`, l.note) : l.note}` : ''}
              </td>
              <td className="px-4 py-1.5">{l.userName ?? ''}</td>
              <td className="px-4 py-1.5">{l.at ? fmtDateTime(l.at) : ''}</td>
            </tr>
          ))}
        </Tabelle>
      )}

      {reiter === 'quarantine' && (
        <>
          <p className="text-sm text-muted-foreground">{t('siteDocs.settings.quarantineText')}</p>
          <Tabelle kopf={[t('siteDocs.settings.received'), t('siteDocs.settings.holder'), t('siteDocs.settings.device'), t('siteDocs.settings.reason'), t('siteDocs.settings.state'), '']} leer={!quarantaene.length}>
            {quarantaene.map((q) => (
              <tr key={q.id} className="border-b border-border last:border-0">
                <td className="px-4 py-1.5">{fmtDateTime(q.receivedAt)}</td>
                <td className="px-4 py-1.5">{q.userName}</td>
                <td className="px-4 py-1.5 font-mono text-xs">{q.deviceId?.slice(0, 8) ?? ''}</td>
                <td className="px-4 py-1.5">{quarantaeneGrund(t, q.reason)}</td>
                <td className="px-4 py-1.5">{q.resolvedAt ? t('siteDocs.settings.resolvedAt', { datum: fmtDateTime(q.resolvedAt) }) : t('siteDocs.settings.open')}</td>
                <td className="px-4 py-1.5 text-right">
                  <button
                    type="button"
                    className="text-xs text-primary hover:underline"
                    onClick={async () => {
                      try {
                        setAnsicht({ zeile: q, inhalt: await fetchQuarantineEntry(q.id) })
                      } catch (e) {
                        setFehler(fehlertext(e, t))
                      }
                    }}
                  >
                    {t('siteDocs.settings.view')}
                  </button>
                </td>
              </tr>
            ))}
          </Tabelle>
        </>
      )}

      {ansicht && (
        <Modal
          isOpen
          onClose={() => setAnsicht(null)}
          title={t('siteDocs.settings.quarantineEntry')}
          size="lg"
          footer={
            <div className="flex w-full justify-between gap-2">
              <button
                type="button"
                className="h-10 rounded-md border px-4 text-sm"
                onClick={() => {
                  const blob = new Blob([JSON.stringify(ansicht.inhalt, null, 2)], { type: 'application/json' })
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `quarantaene-${ansicht.zeile.clientId}.json`
                  a.click()
                  URL.revokeObjectURL(url)
                }}
              >
                {t('siteDocs.settings.download')}
              </button>
              {!ansicht.zeile.resolvedAt && (
                <button
                  type="button"
                  className="h-10 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground"
                  onClick={async () => {
                    try {
                      await resolveQuarantine(ansicht.zeile.id)
                      setAnsicht(null)
                      await lade('quarantine')
                    } catch (e) {
                      setFehler(fehlertext(e, t))
                    }
                  }}
                >
                  {t('siteDocs.settings.resolve')}
                </button>
              )}
            </div>
          }
        >
          <pre className="max-h-[60vh] overflow-auto rounded bg-muted p-3 text-xs">{JSON.stringify(ansicht.inhalt, null, 2)}</pre>
        </Modal>
      )}
    </div>
  )
}
